import React, { useEffect, useState } from 'react';
import culinaryService from '../../services/culinaryService';

/**
 * PlantCookingTab
 * "Cómo cocinarla" tab for a medicinal plant. Lists Wen-inspired curated
 * suggestions: each one has a technique, seasonality, ingredients and steps.
 */
const PlantCookingTab = ({ plantId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inSeasonOnly, setInSeasonOnly] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (!plantId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = await culinaryService.listSuggestionsForPlant(plantId, { inSeasonOnly });
        if (cancelled) return;
        setData(payload);
        if (payload.suggestions?.length) {
          setExpandedId(payload.suggestions[0].id);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.detail || 'No se pudieron cargar las recetas');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [plantId, inSeasonOnly]);

  if (loading) {
    return <p style={styles.helper}>Cargando ideas de cocina…</p>;
  }
  if (error) {
    return <p style={styles.error}>{error}</p>;
  }
  if (!data) return null;

  const hasSuggestions = data.suggestions && data.suggestions.length > 0;

  return (
    <div style={styles.container}>
      <div style={styles.intro}>
        <h3 style={styles.title}>Cómo cocinarla bien</h3>
        <p style={styles.subtitle}>
          Ideas curadas con criterio culinario inspiradas en el método{' '}
          <strong>&laquo;Cocina Inteligente&raquo;</strong>: técnica + nutrición + sabor.
          La técnica importa tanto como el ingrediente.
        </p>
      </div>

      {hasSuggestions && (
        <div style={styles.toolbar}>
          <label style={styles.toggle}>
            <input
              type="checkbox"
              checked={inSeasonOnly}
              onChange={(e) => setInSeasonOnly(e.target.checked)}
            />
            <span>Solo recetas en temporada este mes</span>
          </label>
          <span style={styles.count}>
            {data.suggestion_count} {data.suggestion_count === 1 ? 'idea' : 'ideas'}
          </span>
        </div>
      )}

      {!hasSuggestions ? (
        <p style={styles.helper}>
          Aún no hay recetas curadas para esta planta. Próximamente.
        </p>
      ) : (
        <div style={styles.list}>
          {data.suggestions.map((sug) => {
            const expanded = sug.id === expandedId;
            return (
              <article key={sug.id} style={styles.card}>
                <button
                  type="button"
                  onClick={() => setExpandedId(expanded ? null : sug.id)}
                  style={styles.cardHeader}
                >
                  <div style={styles.cardHeaderText}>
                    <h4 style={styles.cardTitle}>{sug.title}</h4>
                    <p style={styles.cardDescription}>{sug.short_description}</p>
                    <div style={styles.metaRow}>
                      {sug.technique && (
                        <span style={styles.techniqueBadge}>
                          {sug.technique.name_es}
                        </span>
                      )}
                      <span style={styles.metaItem}>
                        ⏱ {sug.prep_time_minutes + sug.cook_time_minutes} min
                      </span>
                      <span style={styles.metaItem}>👥 {sug.servings}</span>
                      {sug.can_be_prep_batch && (
                        <span style={styles.batchBadge}>📦 Base del domingo</span>
                      )}
                      {sug.in_season_now && sug.season_months?.length > 0 && (
                        <span style={styles.seasonBadge}>🌱 En temporada</span>
                      )}
                    </div>
                  </div>
                  <span style={styles.expandIcon}>{expanded ? '−' : '+'}</span>
                </button>

                {expanded && (
                  <div style={styles.cardBody}>
                    <div style={styles.bodyTwoCol}>
                      <div>
                        <h5 style={styles.bodyHeading}>Ingredientes</h5>
                        <ul style={styles.bodyList}>
                          {sug.ingredients.map((ing, idx) => (
                            <li key={idx}>{ing}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h5 style={styles.bodyHeading}>Pasos</h5>
                        <ol style={styles.bodyList}>
                          {sug.steps.map((step, idx) => (
                            <li key={idx}>{step}</li>
                          ))}
                        </ol>
                      </div>
                    </div>

                    {sug.pairs_well_with && sug.pairs_well_with.length > 0 && (
                      <div style={styles.pairings}>
                        <strong>Combina bien con:</strong>{' '}
                        {sug.pairs_well_with.join(', ')}
                      </div>
                    )}

                    <p style={styles.source}>{sug.source}</p>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { display: 'flex', flexDirection: 'column', gap: 16 },
  intro: {},
  title: { margin: 0, fontSize: 18, fontWeight: 600, color: '#111827' },
  subtitle: { marginTop: 4, fontSize: 13, color: '#4b5563', lineHeight: 1.5 },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid #e5e7eb',
  },
  toggle: { display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: '#374151', cursor: 'pointer' },
  count: { fontSize: 12, color: '#6b7280' },
  list: { display: 'flex', flexDirection: 'column', gap: 12 },
  card: {
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    background: '#ffffff',
    overflow: 'hidden',
  },
  cardHeader: {
    width: '100%',
    padding: 14,
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
  },
  cardHeaderText: { flex: 1 },
  cardTitle: { margin: 0, fontSize: 15, fontWeight: 600, color: '#111827' },
  cardDescription: { margin: '4px 0 8px 0', fontSize: 13, color: '#4b5563', lineHeight: 1.4 },
  metaRow: { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' },
  techniqueBadge: {
    background: '#ecfdf5',
    color: '#065f46',
    padding: '2px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
  },
  metaItem: { fontSize: 11, color: '#6b7280' },
  batchBadge: {
    background: '#fef3c7',
    color: '#92400e',
    padding: '2px 8px',
    borderRadius: 999,
    fontSize: 11,
  },
  seasonBadge: {
    background: '#e0e7ff',
    color: '#3730a3',
    padding: '2px 8px',
    borderRadius: 999,
    fontSize: 11,
  },
  expandIcon: { fontSize: 22, color: '#9ca3af', lineHeight: 1 },
  cardBody: {
    padding: 14,
    borderTop: '1px solid #e5e7eb',
    background: '#fafafa',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  bodyTwoCol: { display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 16 },
  bodyHeading: { margin: '0 0 6px 0', fontSize: 13, color: '#111827', textTransform: 'uppercase', letterSpacing: 0.4 },
  bodyList: { margin: 0, paddingLeft: 18, fontSize: 13, color: '#374151', lineHeight: 1.6 },
  pairings: { fontSize: 12, color: '#4b5563', background: '#fff', padding: 8, borderRadius: 6 },
  source: { fontSize: 11, color: '#9ca3af', fontStyle: 'italic', margin: 0 },
  helper: { color: '#6b7280', fontStyle: 'italic' },
  error: { color: '#b91c1c' },
};

export default PlantCookingTab;
